import React from 'react';
import styles from '../ServiceAndRepair.module.scss';
import clsx from 'clsx';
import Chevron from 'assets/icons/chevron.svg?react';
import { GroupedData, ServiceAndRepairCategory } from '../ServiceAndRepair.tsx';
import Cloud from 'assets/icons/cloud-icon.svg?react';
import GreyCloud from 'assets/icons/cloud-icon-grey.svg';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { downloadOrOpenBlobFile } from '../../../../helpers/download-helper.ts';
import animationData from '../../../../assets/animations/loader_small.json';
import { LottieContainer } from '../../../../components/animations/LottieContainer.tsx';
import { useProcessLinks } from '../../../../helpers/useProcessLinks.ts';
import { Tooltip } from '../../../../components/uikit/Tooltip.tsx';
import { Trans, useTranslation } from 'react-i18next';
import { useAnalytics } from 'helpers/analytics.ts';
import { Tip } from '../../../../components/uikit/Tip.tsx';
import { tDynamic } from '../../../../helpers/tDynamic.ts';

type OwnProps = {
  groupedData: GroupedData;
  closedOrders: Set<string>;
  handleClickAccordion: (key: string) => void;
  languages: string[];
  openAllAccordions: () => void;
  closeAllAccordions: () => void;
};

const mail = 'supportHC@loewensteinmedical.com';
const none = 'none';

export const Accordion = (props: OwnProps) => {
  const {
    groupedData,
    closedOrders,
    handleClickAccordion,
    languages,
    openAllAccordions,
    closeAllAccordions,
  } = props;

  const i18n = useScopedTranslation('Page.ServiceAndRepair');
  const i18nCommon = useTranslation();
  const { trackEvent } = useAnalytics();

  const {
    addProcessingLink,
    removeProcessingLink,
    processingLinks,
    showTooltip,
    isTooltipVisible,
    closeTooltip,
  } = useProcessLinks();

  const outerTableTextStyles = [
    styles.text,
    styles.overflowEllipsis,
    styles.cursorDefault,
  ];

  const keys = Object.keys(groupedData);

  const isAllAccordionClosed = closedOrders.size === keys.length;

  return (
    <div className={styles.marginBottom65}>
      {!!Object.keys(groupedData).length && (
        <div className={styles.expandAll}>
          <Chevron
            data-test-id={'expand-all'}
            className={clsx(
              styles.pointer,
              styles.marginTop5,
              isAllAccordionClosed
                ? styles.rotate180
                : styles.marginBottomMinus2,
            )}
            onClick={() => {
              if (isAllAccordionClosed) {
                openAllAccordions();
              } else {
                closeAllAccordions();
              }
            }}
          />
        </div>
      )}
      {keys.map((key, index) => (
        <div key={key}>
          <div
            className={clsx(
              styles.rowContainer,
              styles.accordion,
              closedOrders.has(key) &&
                index !== keys.length - 1 &&
                styles.borderBottom,
            )}
          >
            <div>
              <Chevron
                data-test-id={'expand-one'}
                className={clsx(
                  styles.pointer,
                  closedOrders.has(key)
                    ? styles.rotate180
                    : styles.marginBottomMinus2,
                )}
                onClick={() => handleClickAccordion(key)}
              />
            </div>
            <div
              data-test-id={'group-id'}
              className={clsx(outerTableTextStyles, styles.id)}
            >
              {key.replace('_', '')}
            </div>
            <div
              data-test-id={'group-name'}
              className={clsx(outerTableTextStyles, styles.flex1)}
            >
              {groupedData[key][0].groupName.replace(/^\d+\s*/, '')}
            </div>
          </div>
          {!closedOrders.has(key) && (
            <div>
              {groupedData[key].map((item, index) => (
                <div
                  data-test-id={'item-container'}
                  key={index}
                  className={clsx(
                    styles.rowContainer,
                    styles.text,
                    styles.paddingTopBottom14,
                    styles.rowAccordion,
                  )}
                >
                  <div data-test-id={'item-name'} className={styles.flex1}>
                    {item.name}
                  </div>
                  <div className={styles.rowContainer}>
                    {languages.map((language) => {
                      const languagesSet = new Set<string>();

                      return (
                        <div
                          className={clsx(styles.width88, styles.displayFlex)}
                          key={language}
                        >
                          {item.documentDownloadLinks.map((link) => {
                            const translation = tDynamic(i18n, language, {
                              lng: 'en',
                            });

                            if (
                              link.language === translation &&
                              !languagesSet.has(translation)
                            ) {
                              languagesSet.add(translation);

                              const links: string[] = [];
                              let isLoading = false;

                              item.documentDownloadLinks.forEach(
                                (documentLink) => {
                                  if (documentLink.language === translation) {
                                    links.push(documentLink.link);

                                    const key = `${documentLink.link}-${item.groupId}`;

                                    if (processingLinks.has(key)) {
                                      isLoading = true;
                                    }
                                  }
                                },
                              );

                              const isLinkEmpty =
                                links.length === 1 && links[0] === none;

                              return (
                                <div
                                  data-test-id={translation}
                                  key={language}
                                  className={styles.rowContainer}
                                >
                                  {isLoading ? (
                                    <LottieContainer
                                      animationData={animationData}
                                      segments={
                                        isLoading ? [1, 180] : [180, 270]
                                      }
                                      loop={isLoading}
                                      width={24}
                                      height={24}
                                    />
                                  ) : isLinkEmpty ? (
                                    <Tip
                                      tooltipText={i18nCommon.t(
                                        'link_unavailable_msg',
                                      )}
                                      customImage={GreyCloud}
                                      customImageClassname={styles.emptyLink}
                                      className={styles.tip}
                                    />
                                  ) : (
                                    <Cloud
                                      className={clsx(styles.link)}
                                      onClick={() => {
                                        links.forEach((link) => {
                                          const key = `${link}-${item.groupId}`;
                                          trackEvent({
                                            category: ServiceAndRepairCategory,
                                            action: 'Download file',
                                            name: link,
                                          });
                                          addProcessingLink(key);
                                          downloadOrOpenBlobFile(
                                            link,
                                            item.name,
                                          )
                                            .catch(showTooltip)
                                            .finally(() =>
                                              removeProcessingLink(key),
                                            );
                                        });
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            }
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {isTooltipVisible && (
        <Tooltip
          isError={true}
          closeTooltip={closeTooltip}
          bodyContent={
            <Trans
              i18nKey={'fileNotFound'}
              components={{
                supportLink: <a href={`mailto:${mail}`} />,
              }}
            />
          }
          className={styles.tooltip}
          showCancelButton={true}
          iconClassName={styles.tooltipIcon}
        />
      )}
    </div>
  );
};
